import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, ClockCounterClockwise, Trash, ShieldCheck } from "@phosphor-icons/react";
import { api, formatApiError, ROLE_LABELS, formatDateTime } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [histUser, setHistUser] = useState(null);
  const [permMeta, setPermMeta] = useState([]);
  const [permUser, setPermUser] = useState(null);     // user being edited
  const [permSel, setPermSel] = useState([]);          // selected permission keys

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/permissions/meta").then((r) => setPermMeta(r.data.permissions)).catch(() => {});
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPerms = (u) => {
    setPermUser(u);
    setPermSel([...(u.permissions || [])]);
  };

  const togglePerm = (key) => {
    setPermSel((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  };

  const savePerms = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${permUser.id}`, { permissions: permSel });
      toast.success("Permissions updated");
      setPermUser(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.name}" (${u.email}) permanently?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4 stagger-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-slate-900">Team &amp; permissions</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="active:scale-95"><UserPlus size={16} className="mr-1" /> Add User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Team User</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Name</Label>
                <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email</Label>
                <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Password</Label>
                <Input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="create-user-submit" className="w-full" disabled={!form.name || !form.email || !form.password || !form.role || busy} onClick={create}>Create User</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-sm text-muted-foreground">Assign each team member the exact powers they need. Super Admin always has full access.</p>

      <div className="border border-slate-200/80 rounded-2xl bg-card shadow-[0_1px_3px_rgba(15,23,42,0.04)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <th className="py-2 px-3 font-bold">Name</th>
              <th className="py-2 px-3 font-bold">Email</th>
              <th className="py-2 px-3 font-bold">Role</th>
              <th className="py-2 px-3 font-bold">Password</th>
              <th className="py-2 px-3 font-bold">Active</th>
              <th className="py-2 px-3 font-bold">Powers</th>
              <th className="py-2 px-3 font-bold">Delete</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border hover:bg-muted transition-colors" data-testid={`user-row-${u.email}`}>
                <td className="py-2 px-3 font-semibold">{u.name}</td>
                <td className="py-2 px-3 font-mono text-xs">{u.email}</td>
                <td className="py-2 px-3">{ROLE_LABELS[u.role]}</td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs" data-testid={`password-${u.email}`}>{u.current_password || "—"}</span>
                    {(u.password_history || []).length > 0 && (
                      <button type="button" data-testid={`password-history-${u.email}`} onClick={() => setHistUser(u)}
                        className="text-muted-foreground hover:text-foreground p-1" title="Password history">
                        <ClockCounterClockwise size={14} />
                      </button>
                    )}
                  </div>
                </td>
                <td className="py-2 px-3">
                  {u.role === "super_admin" ? <span className="text-xs text-muted-foreground">—</span> :
                    <Switch data-testid={`toggle-active-${u.email}`} checked={u.active} onCheckedChange={() => toggleActive(u)} />}
                </td>
                <td className="py-2 px-3">
                  {u.role === "super_admin" ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><ShieldCheck size={14} /> Full access</span>
                  ) : (
                    <button type="button" data-testid={`edit-perms-${u.email}`} onClick={() => openPerms(u)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 border border-violet-200 bg-violet-50 hover:bg-violet-100 rounded-full px-3 py-1 transition-colors active:scale-95">
                      <ShieldCheck size={14} /> {(u.permissions || []).length} {(u.permissions || []).length === 1 ? "power" : "powers"} · Edit
                    </button>
                  )}
                </td>
                <td className="py-2 px-3">
                  {u.role === "super_admin" ? <span className="text-xs text-muted-foreground">—</span> : (
                    <button type="button" data-testid={`delete-user-${u.email}`} onClick={() => deleteUser(u)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1.5 transition-colors active:scale-90" title="Delete user">
                      <Trash size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Permissions editor */}
      <Dialog open={!!permUser} onOpenChange={(v) => !v && setPermUser(null)}>
        <DialogContent className="max-w-md" data-testid="permissions-dialog">
          <DialogHeader>
            <DialogTitle>Powers — {permUser?.name}</DialogTitle>
            <DialogDescription>Turn each power on or off for this {permUser ? ROLE_LABELS[permUser.role] : ""}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-[55vh] overflow-y-auto -mx-1 px-1">
            {[...new Set(permMeta.map((p) => p.group || "General"))].map((g) => (
              <div key={g} data-testid={`perm-group-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="px-3 pt-3 pb-1 text-[10.5px] uppercase tracking-[0.14em] font-bold text-brand-700">{g}</div>
                {permMeta.filter((p) => (p.group || "General") === g).map((p) => (
                  <label key={p.key} data-testid={`perm-row-${p.key}`}
                    className="flex items-center justify-between gap-3 py-2.5 px-3 rounded-lg hover:bg-muted cursor-pointer">
                    <span className="text-sm font-medium">{p.label}</span>
                    <Switch data-testid={`perm-toggle-${p.key}`} checked={permSel.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} />
                  </label>
                ))}
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setPermUser(null)}>Cancel</Button>
            <Button data-testid="save-perms-btn" className="rounded-full px-5 font-semibold" disabled={busy} onClick={savePerms}>Save Powers</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password history */}
      <Dialog open={!!histUser} onOpenChange={(v) => !v && setHistUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Password History — {histUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="password-history-panel">
            {[...(histUser?.password_history || [])].reverse().map((p, i) => (
              <div key={i} className="flex items-center justify-between border border-border rounded p-2.5 text-sm">
                <span className="font-mono font-semibold">{p.password}</span>
                <span className="text-[11px] text-muted-foreground text-right">
                  {p.set_by === "self" ? "Changed by user" : "Set by admin"}<br />
                  <span className="font-mono">{formatDateTime(p.at)}</span>
                </span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
